import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Standard portal button. Use this instead of hand-rolled button classes so
 * variants, focus rings, and disabled states stay consistent everywhere.
 *
 * <Button variant="primary" onClick={...}>Submit Order</Button>
 * <Button variant="danger" size="sm" loading={saving}>Delete</Button>
 * <Button variant="ghost" ariaLabel="Close" icon={<X className="h-5 w-5" />} />
 */
const VARIANTS = {
  primary:
    'bg-brand text-brand-dark hover:bg-brand-hover',
  secondary:
    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
  danger:
    'bg-red-600 text-white hover:bg-red-700',
  ghost:
    'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  // Icon-only row deletes: quiet until hovered, so a list of rows isn't a column of red.
  ghostDanger:
    'bg-transparent text-gray-400 hover:bg-red-50 hover:text-red-600',
};

const SIZES = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon = null,
  ariaLabel,
  type = 'button',
  className = '',
  children,
  ...props
}) {
  const isIconOnly = icon && !children;
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium
        transition-colors duration-200 cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand
        disabled:opacity-50 disabled:cursor-not-allowed
        ${VARIANTS[variant] || VARIANTS.primary}
        ${isIconOnly ? 'p-2' : SIZES[size] || SIZES.md}
        ${className}`}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export default Button;
